import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { addCmsPage, editCmsPage, cmsPageDetails, contactUsListing } from "../apis/apiHandler";

// Helper: always extract a string from any error shape
const extractError = (payload, fallback = 'Something went wrong') => {
    if (!payload) return fallback;
    if (typeof payload === 'string') return payload;
    if (typeof payload?.message === 'string') return payload.message;
    if (typeof payload?.message?.keyword === 'string') return payload.message.keyword;
    return fallback;
};

// Get single page details by tag_name
export const getCmsPageDetails = createAsyncThunk(
    'cms/view',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await cmsPageDetails(payload);
            if (response?.code === 200) {
                return response;
            } else {
                return rejectWithValue(response);
            }
        } catch (error) {
            return rejectWithValue(error?.response?.data || error.message);
        }
    }
)

// Add new CMS page
export const addCms = createAsyncThunk(
    'cms/add',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await addCmsPage(payload);
            if (response?.code === 200) {
                return response;
            } else {
                return rejectWithValue(response);
            }
        } catch (error) {
            return rejectWithValue(error?.response?.data || error.message);
        }
    }
)

// Edit existing CMS page
export const editCms = createAsyncThunk(
    'cms/edit',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await editCmsPage(payload);
            if (response?.code === 200) {
                return response;
            } else {
                return rejectWithValue(response);
            }
        } catch (error) {
            return rejectWithValue(error?.response?.data || error.message);
        }
    }
)

// Composite: edit first, fallback to add
export const addUpdateCms = createAsyncThunk(
    'cms/addUpdate',
    async (payload, { dispatch, rejectWithValue }) => {
        try {
            const editResponse = await dispatch(editCms(payload)).unwrap();
            return editResponse;
        } catch {
            try {
                const addResponse = await dispatch(addCms(payload)).unwrap();
                return addResponse;
            } catch (addError) {
                return rejectWithValue(addError);
            }
        }
    }
)

// Composite: edit first, fallback to add
export const contactUsList = createAsyncThunk(
    'cms/contact',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await contactUsListing(payload);
            if (response?.code == 200) {
                return response;
            } else {
                return rejectWithValue(response);
            }
        } catch (error) {
            return rejectWithValue(error?.response?.data || error.message);
        }
    }
)

const initialState = {
    loading: false,
    cmsDetail: {},
    error: null,
    saveSuccess: false,
    contactList: []
};

const cmsSlice = createSlice({
    name: 'cms',
    initialState,
    reducers: {
        resetSaveSuccess: (state) => {
            state.saveSuccess = false;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // view
            .addCase(getCmsPageDetails.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getCmsPageDetails.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.cmsDetail = payload?.data || {};
            })
            .addCase(getCmsPageDetails.rejected, (state, action) => {
                state.loading = false;
                // Store as string — no_data_found is expected on first load, not a real error
                state.error = null;
                state.cmsDetail = {};
            })

            // add
            .addCase(addCms.pending, (state) => {
                state.loading = true;
                state.saveSuccess = false;
                state.error = null;
            })
            .addCase(addCms.fulfilled, (state) => {
                state.loading = false;
                state.saveSuccess = true;
            })
            .addCase(addCms.rejected, (state, action) => {
                state.loading = false;
                state.saveSuccess = false;
                state.error = extractError(action.payload);
            })

            // edit
            .addCase(editCms.pending, (state) => {
                state.loading = true;
                state.saveSuccess = false;
                state.error = null;
            })
            .addCase(editCms.fulfilled, (state) => {
                state.loading = false;
                state.saveSuccess = true;
            })
            .addCase(editCms.rejected, (state, action) => {
                state.loading = false;
                state.saveSuccess = false;
                state.error = extractError(action.payload);
            })

            // addUpdate
            .addCase(addUpdateCms.pending, (state) => {
                state.loading = true;
                state.saveSuccess = false;
                state.error = null;
            })
            .addCase(addUpdateCms.fulfilled, (state) => {
                state.loading = false;
                state.saveSuccess = true;
            })
            .addCase(addUpdateCms.rejected, (state, action) => {
                state.loading = false;
                state.saveSuccess = false;
                state.error = extractError(action.payload);
            })
            .addCase(contactUsList.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(contactUsList.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.contactList = payload.data;
            })
            .addCase(contactUsList.rejected, (state, action) => {
                state.loading = false;
                state.error = extractError(action.payload);
            })
    }
})

export const { resetSaveSuccess } = cmsSlice.actions;
export default cmsSlice.reducer;